import { FormEvent, ReactNode, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ROLE_HOME, useAuth } from "../auth/AuthProvider";
import { errorMessage } from "../api/client";
import { Field, PasswordInput } from "../components/common";

export const ERR_CLS = "rounded-lg border border-[#FFC400] bg-[#FFF4CC] p-3 text-sm text-[#003F32]";

function BrandHero({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`relative overflow-hidden bg-[#003F32] ${compact ? "h-64" : "h-full"}`} data-testid="auth-brand-hero">
      <img src="/brand-hero.jpg" alt="FastPass patient transport team" className="absolute inset-0 h-full w-full object-cover object-[60%_center]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#003F32] via-[#003F32]/55 to-[#003F32]/10" />
      <div className={`relative flex h-full flex-col justify-end ${compact ? "p-6" : "p-12 xl:p-16"}`}>
        <span className="mb-4 inline-flex w-fit items-center gap-2 rounded-full bg-[#FFC400] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#003F32]">Metro Cebu · Philippines</span>
        <h2 className={`font-display font-extrabold leading-[1.05] text-white ${compact ? "text-4xl" : "text-5xl xl:text-6xl"}`}>
          Every <span className="text-[#FFC400]">Minute</span> Counts.
        </h2>
        <p className={`mt-3 font-medium text-white/90 ${compact ? "text-sm" : "text-lg"}`}>Faster Access. Closer Care. A Safer Philippines.</p>
      </div>
    </div>
  );
}

export function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#CDEBDD] via-[#F1F9F4] to-[#FFEBA8] lg:grid lg:grid-cols-[1.15fr_1fr]" data-testid="auth-layout">
      <div className="hidden lg:block"><div className="sticky top-0 h-screen"><BrandHero /></div></div>
      <div className="lg:hidden"><BrandHero compact /></div>
      <div className="safe-x safe-bottom relative -mt-6 rounded-t-3xl bg-gradient-to-br from-[#CDEBDD] via-[#F1F9F4] to-[#FFEBA8] lg:mt-0 lg:flex lg:items-center lg:rounded-none" data-testid="auth-form-panel">
        <div className="mx-auto w-full max-w-md px-6 py-8 lg:py-12">
          <Link to="/" className="mb-8 inline-flex rounded-2xl bg-[#036248] px-4 py-3" aria-label="FastPass home" data-testid="auth-logo">
            <img src="/logo-wordmark.png" alt="FastPass" className="h-10 w-auto" />
          </Link>
          <h1 className="text-3xl font-extrabold text-[#003F32] sm:text-4xl">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-[#036248]">{subtitle}</p>}
          <div className="mt-7">{children}</div>
          <p className="mt-8 flex items-center gap-2 rounded-xl bg-white/70 px-4 py-3 text-sm text-[#003F32]" data-testid="auth-emergency-note">
            <span className="h-2 w-2 rounded-full bg-[#FFC400]" /> Life-threatening emergency? Call <a href="tel:911" className="font-bold underline">911</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={ROLE_HOME[user.role]} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const u = await login(email, password); nav(ROLE_HOME[u.role], { replace: true }); }
    catch (ex) { setErr(errorMessage(ex)); }
    finally { setBusy(false); }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to request transport, track trips and manage your membership.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Username"><input className="fp-input" autoComplete="username" autoCapitalize="none" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-username-input" /></Field>
        <Field label="Password"><PasswordInput autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" /></Field>
        <div className="-mt-1 text-right"><Link to="/forgot-password" className="text-sm font-semibold text-[#036248] underline" data-testid="login-forgot-link">Forgot password?</Link></div>
        {err && <p role="alert" className={ERR_CLS} data-testid="login-error">{err}</p>}
        <button className="fp-btn h-12 w-full bg-[#003F32] text-base text-white hover:bg-[#036248]" disabled={busy} data-testid="login-submit-button">
          {busy && <Loader2 size={16} className="animate-spin" />} Sign in
        </button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-[#036248]"><span className="h-px flex-1 bg-[#036248]/20" />New to FASTPASS?<span className="h-px flex-1 bg-[#036248]/20" /></div>
      <Link to="/register" className="fp-btn h-12 w-full bg-[#FFC400] text-base text-[#003F32] hover:bg-[#F4DF8C]" data-testid="login-register-link">Get FastPass — create patient account →</Link>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={ROLE_HOME[user.role]} replace />;
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await register(f); nav("/patient", { replace: true }); }
    catch (ex) { setErr(errorMessage(ex)); }
    finally { setBusy(false); }
  };

  return (
    <AuthLayout title="Get FastPass" subtitle="Create your patient account in under a minute.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Full name"><input className="fp-input" required autoComplete="name" value={f.name} onChange={set("name")} data-testid="register-name-input" /></Field>
        <Field label="Email"><input className="fp-input" type="email" required autoComplete="email" value={f.email} onChange={set("email")} data-testid="register-email-input" /></Field>
        <Field label="Mobile number" hint="Philippine mobile, e.g. 0917 123 4567 or +63 917 123 4567">
          <input className="fp-input" type="tel" required autoComplete="tel" inputMode="tel" value={f.phone} onChange={set("phone")} data-testid="register-phone-input" />
        </Field>
        <Field label="Password" hint="At least 8 characters"><PasswordInput required minLength={8} autoComplete="new-password" value={f.password} onChange={set("password")} data-testid="register-password-input" /></Field>
        {err && <p role="alert" className={ERR_CLS} data-testid="register-error">{err}</p>}
        <button className="fp-btn h-12 w-full bg-[#FFC400] text-base text-[#003F32] hover:bg-[#F4DF8C]" disabled={busy} data-testid="register-submit-button">
          {busy && <Loader2 size={16} className="animate-spin" />} Create account
        </button>
      </form>
      <p className="mt-5 text-sm text-[#024634]">Already registered? <Link to="/" className="font-semibold text-[#036248] underline" data-testid="register-signin-link">Sign in</Link></p>
      <p className="mt-2 text-xs text-[#036248]/80">Responder and dispatcher accounts are created by FASTPASS administrators.</p>
    </AuthLayout>
  );
}
